import React, { useState, useMemo } from "react";
import styled from "styled-components";
import PageLayout from "lib/components/PageLayout";
import ContentArea from "lib/components/ContentArea";
import Head from "lib/components/Head";
import useIsResMobile from "lib/hooks/useIsResMobile";

// Scenario data based on FY 2026 H-1B lottery
// Registrations by level calculated as percentages: Level I = 40%, Level II = 42%, Level III = 11%, Level IV = 7%
const SCENARIO_DATA = {
  randomized: {
    name: "Randomized (Old System)",
    description: "Old lottery system with equal odds for all wage levels",
    totalRegistrations: 343981,
    totalSelections: 120141,
    isRandomized: true, // Flag to use flat probability
    flatProbability: 34.9, // 34.9% for all levels
    registrationsByLevel: {
      levelI: 137592, // 40% of 343,981
      levelII: 144472, // 42% of 343,981
      levelIII: 37838, // 11% of 343,981
      levelIV: 24079, // 7% of 343,981
    },
  },
  baseline: {
    name: "Baseline",
    description: "No change in total registrations",
    totalRegistrations: 343981,
    totalSelections: 120141,
    registrationsByLevel: {
      levelI: 137592, // 40% of 343,981
      levelII: 144472, // 42% of 343,981
      levelIII: 37838, // 11% of 343,981
      levelIV: 24079, // 7% of 343,981
    },
  },
  scenarioA: {
    name: "15% Drop",
    description: "15% drop in total registrations",
    totalRegistrations: 292384,
    totalSelections: 120141,
    registrationsByLevel: {
      levelI: 116954, // 40% of 292,384
      levelII: 122801, // 42% of 292,384
      levelIII: 32162, // 11% of 292,384
      levelIV: 20467, // 7% of 292,384
    },
  },
  scenarioB: {
    name: "30% Drop",
    description: "30% drop in total registrations",
    totalRegistrations: 240787,
    totalSelections: 120141,
    registrationsByLevel: {
      levelI: 96315, // 40% of 240,787
      levelII: 101131, // 42% of 240,787
      levelIII: 26487, // 11% of 240,787
      levelIV: 16854, // 7% of 240,787
    },
  },
  scenarioD: {
    name: "50% Drop",
    description: "50% drop in total registrations",
    totalRegistrations: 171991,
    totalSelections: 120141,
    registrationsByLevel: {
      levelI: 68796, // 40% of 171,991
      levelII: 72236, // 42% of 171,991
      levelIII: 18919, // 11% of 171,991
      levelIV: 12040, // 7% of 171,991
    },
  },
};

const PageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 40px 0;
`;

const SimulatorCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 40px 20px 0;
  color: #1a1a1a;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #1a1a1a;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  margin-bottom: 8px;
  white-space: nowrap;
`;

const Input = styled.input`
  height: 48px;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  transition: border-color 0.2s;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #195dee;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const ScenarioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const ScenarioBox = styled.div<{ isSelected: boolean; isDisabled?: boolean }>`
  padding: 24px;
  border: 2px solid
    ${(props) =>
      props.isDisabled ? "#e2e8f0" : props.isSelected ? "#195dee" : "#e2e8f0"};
  border-radius: 8px;
  text-align: center;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  background: ${(props) =>
    props.isDisabled ? "#f1f5f9" : props.isSelected ? "#f0f6ff" : "white"};
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(props) => (props.isDisabled ? "#e2e8f0" : "#195dee")};
    box-shadow: ${(props) =>
      props.isDisabled ? "none" : "0 4px 12px rgba(25, 93, 238, 0.1)"};
  }
`;

const ScenarioLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 12px;
`;

const ScenarioValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
`;

const WageLevelRow = styled.div`
  display: grid;
  grid-template-columns: auto repeat(4, 1fr);
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const WageLevelLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  min-width: 140px;
`;

const DataBox = styled.div`
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e2e8f0;
`;

const DataValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`;

const OddsBox = styled(DataBox)`
  background: #f0f6ff;
  border-color: #195dee;
`;

const OddsValue = styled(DataValue)`
  color: #195dee;
`;

const InfoSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #1a1a1a;
`;

const InfoText = styled.p`
  font-size: 14px;
  line-height: 1.67;
  color: #475569;
  margin: 0 0 12px 0;
`;

const InfoList = styled.ul`
  margin: 12px 0;
  padding-left: 24px;
`;

const InfoListItem = styled.li`
  font-size: 14px;
  line-height: 1.67;
  color: #475569;
  margin-bottom: 8px;
`;

const CustomScenarioContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 32px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #195dee;
`;

const CheckboxLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Slider = styled.input`
  flex: 1;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: #e2e8f0;
  border-radius: 4px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    background: #195dee;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.2s;
  }

  &::-webkit-slider-thumb:hover {
    background: #1347b8;
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    background: #195dee;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const SliderValue = styled.div`
  min-width: 140px;
  padding: 12px 16px;
  background: white;
  border: 2px solid #195dee;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #195dee;
  text-align: center;
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #64748b;
`;

const CustomBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BreakdownItem = styled.div`
  padding: 12px;
  background: white;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #e2e8f0;
`;

const BreakdownLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
`;

const BreakdownValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
`;

const ExplanationSection = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  border-left: 4px solid #195dee;
`;

const ExplanationTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #195dee;
  margin: 0 0 8px 0;
`;

const ExplanationText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
  margin: 0 0 16px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ProcessStep = styled.div`
  margin-bottom: 12px;
`;

const ProcessStepTitle = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const NewsletterLink = styled.a`
  color: #195dee;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #1347b8;
  }
`;

const H1BLotterySimulator = () => {
  const isResMobile = useIsResMobile();
  const [selectedScenario, setSelectedScenario] = useState<
    "randomized" | "baseline" | "scenarioA" | "scenarioB" | "scenarioD"
  >("baseline");

  const [userEntries, setUserEntries] = useState({
    levelI: 10,
    levelII: 10,
    levelIII: 10,
    levelIV: 10,
  });

  // Custom scenario state
  const [isCustomScenario, setIsCustomScenario] = useState(false);
  const [customTotalRegistrations, setCustomTotalRegistrations] =
    useState(343981);

  // Update custom value to match selected preset when enabling custom mode
  const handleCustomToggle = (enabled: boolean) => {
    if (enabled) {
      // Set custom value to match the currently selected preset
      setCustomTotalRegistrations(
        SCENARIO_DATA[selectedScenario].totalRegistrations,
      );
    }
    setIsCustomScenario(enabled);
  };

  // Calculate custom registrations by level using fixed percentages
  const customRegistrationsByLevel = useMemo(
    () => ({
      levelI: Math.round(customTotalRegistrations * 0.4),
      levelII: Math.round(customTotalRegistrations * 0.42),
      levelIII: Math.round(customTotalRegistrations * 0.11),
      levelIV: Math.round(customTotalRegistrations * 0.07),
    }),
    [customTotalRegistrations],
  );

  // Calculate weighted lottery results
  const calculateResults = useMemo(() => {
    const scenario = SCENARIO_DATA[selectedScenario];
    const totalSelections = 120141; // Fixed number of selections
    const registrations = isCustomScenario
      ? customRegistrationsByLevel
      : scenario.registrationsByLevel;

    // Calculate total unique registrations in the pool
    const totalUniqueRegistrations =
      registrations.levelI +
      registrations.levelII +
      registrations.levelIII +
      registrations.levelIV;

    const totalUserEntries =
      userEntries.levelI +
      userEntries.levelII +
      userEntries.levelIII +
      userEntries.levelIV;

    // Check if this is the randomized (old system) scenario
    const isRandomizedScenario =
      selectedScenario === "randomized" && !isCustomScenario;

    let levelIProb: number;
    let levelIIProb: number;
    let levelIIIProb: number;
    let levelIVProb: number;

    if (isRandomizedScenario) {
      // Old system: flat 34.9% probability for all levels
      const flatProb = 0.349;
      levelIProb = flatProb;
      levelIIProb = flatProb;
      levelIIIProb = flatProb;
      levelIVProb = flatProb;
    } else {
      // WEIGHTED lottery
      // Calculate total weighted entries in the pool
      // Each registration is counted multiple times based on its wage level weight
      const totalWeightedEntries =
        registrations.levelI * 1 +
        registrations.levelII * 2 +
        registrations.levelIII * 3 +
        registrations.levelIV * 4;

      // Calculate probability for a single unique registration at each level
      // Each weighted entry has equal chance: totalSelections / totalWeightedEntries
      // Handle edge case when totalWeightedEntries is 0 or very small
      const baseSelectionProb =
        totalWeightedEntries > 0
          ? Math.min(1, totalSelections / totalWeightedEntries)
          : 1;

      // For more accurate probability when selection rates are high, use: 1 - (1 - baseProb)^weight
      // Clamp probabilities between 0 and 1 to handle edge cases
      levelIProb = Math.max(0, Math.min(1, 1 * baseSelectionProb));
      levelIIProb = Math.max(
        0,
        Math.min(1, 1 - Math.pow(1 - baseSelectionProb, 2)),
      );
      levelIIIProb = Math.max(
        0,
        Math.min(1, 1 - Math.pow(1 - baseSelectionProb, 3)),
      );
      levelIVProb = Math.max(
        0,
        Math.min(1, 1 - Math.pow(1 - baseSelectionProb, 4)),
      );
    }

    // Calculate total weighted entries for display purposes
    const totalWeightedEntries =
      registrations.levelI * 1 +
      registrations.levelII * 2 +
      registrations.levelIII * 3 +
      registrations.levelIV * 4;

    // Calculate expected selections by level (capped at the number of entries per level)
    const userSelectionsByLevel = {
      levelI: Math.min(userEntries.levelI, userEntries.levelI * levelIProb),
      levelII: Math.min(userEntries.levelII, userEntries.levelII * levelIIProb),
      levelIII: Math.min(
        userEntries.levelIII,
        userEntries.levelIII * levelIIIProb,
      ),
      levelIV: Math.min(userEntries.levelIV, userEntries.levelIV * levelIVProb),
    };

    // Calculate total expected selections
    // This represents the expected number of unique beneficiaries selected
    // We need to account for the fact that once selected, they're removed
    let expectedSelections = 0;
    let remainingEntries = totalUserEntries;

    // Process from highest to lowest wage level (most likely to be selected first)
    const levels = [
      {
        count: userEntries.levelIV,
        prob: levelIVProb,
        selections: userSelectionsByLevel.levelIV,
      },
      {
        count: userEntries.levelIII,
        prob: levelIIIProb,
        selections: userSelectionsByLevel.levelIII,
      },
      {
        count: userEntries.levelII,
        prob: levelIIProb,
        selections: userSelectionsByLevel.levelII,
      },
      {
        count: userEntries.levelI,
        prob: levelIProb,
        selections: userSelectionsByLevel.levelI,
      },
    ];

    levels.forEach((level) => {
      if (remainingEntries > 0 && level.count > 0) {
        const selectionsFromThisLevel = Math.min(
          level.selections,
          remainingEntries,
        );
        expectedSelections += selectionsFromThisLevel;
        remainingEntries -= selectionsFromThisLevel;
      }
    });

    return {
      totalWeightedEntries,
      totalUniqueRegistrations,
      expectedSelections: Math.max(
        0,
        Math.round(Math.min(expectedSelections, totalUserEntries)),
      ),
      totalUserEntries,
      userSelectionsByLevel,
      levelProbabilities: {
        levelI: Math.max(0, levelIProb * 100),
        levelII: Math.max(0, levelIIProb * 100),
        levelIII: Math.max(0, levelIIIProb * 100),
        levelIV: Math.max(0, levelIVProb * 100),
      },
    };
  }, [
    selectedScenario,
    userEntries,
    isCustomScenario,
    customRegistrationsByLevel,
  ]);

  const handleInputChange = (
    level: keyof typeof userEntries,
    value: string,
  ) => {
    // Remove leading zeros by parsing and re-stringifying
    const numValue = parseInt(value, 10);
    setUserEntries((prev) => ({
      ...prev,
      [level]: isNaN(numValue) ? 0 : Math.max(0, numValue),
    }));
  };

  // Format display value to avoid leading zeros
  const getDisplayValue = (value: number): string => {
    return value === 0 ? "" : value.toString();
  };

  const currentScenario = SCENARIO_DATA[selectedScenario];

  return (
    <PageLayout>
      <Head
        title="H-1B Weighted Lottery Simulator | Lawfully"
        desc="Simulate your odds in the weighted H-1B lottery under different scenarios. Based on Lawfully Intelligence analysis of the proposed weighted selection rule."
        url="/data/h1b-lottery-simulator"
      />
      <PageContainer>
        <ContentArea>
          <SimulatorCard>
            <Title>Weighted H-1B Lottery Simulator</Title>

            <ExplanationSection>
              <ExplanationTitle>Summary</ExplanationTitle>
              <ExplanationText>
                USCIS announced the transition to a{" "}
                <NewsletterLink
                  href="https://www.federalregister.gov/documents/2025/12/29/2025-23853/weighted-selection-process-for-registrants-and-petitioners-seeking-to-file-cap-subject-h-1b"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Weighted Selection
                </NewsletterLink>{" "}
                system for this year&apos;s H-1B lottery. This tool helps
                organizations simulate their selection odds under this new
                system.
              </ExplanationText>

              <ExplanationTitle>Why it matters</ExplanationTitle>
              <ExplanationText>
                The rule presents a completely new reality for companies. Gone
                will be the days of randomized selection. In its place, there
                will be real strategy involved in increasing selection odds—but
                that strategy will involve companies considering significant
                changes to their sponsorship policies and current best
                practices.
              </ExplanationText>

              <ExplanationTitle>Timing matters</ExplanationTitle>
              <ExplanationText>
                In order to execute on those changes in time for the FY 2027
                H-1B cap registration, HR and global mobility teams will need to
                start now.
              </ExplanationText>

              <ExplanationTitle>
                How the weighted lottery changes the process
              </ExplanationTitle>
              <ProcessStep>
                <ExplanationText>
                  <ProcessStepTitle>Pre-Registration:</ProcessStepTitle>{" "}
                  Employers determine the SOC code, work location, and
                  corresponding prevailing wage level (I–IV) under the OEWS
                  system based on the proffered wage they intend to pay each
                  applicant. If multiple worksites or pay ranges exist, the
                  employer must choose the lowest corresponding wage level.
                </ExplanationText>
              </ProcessStep>
              <ProcessStep>
                <ExplanationText>
                  <ProcessStepTitle>Registration:</ProcessStepTitle> The new
                  online registration form will include a checkbox or dropdown
                  for wage level and certification of a bona fide job offer. No
                  proof is uploaded.
                </ExplanationText>
              </ProcessStep>
              <ProcessStep>
                <ExplanationText>
                  <ProcessStepTitle>Selection:</ProcessStepTitle> Under the
                  rule, each unique beneficiary is entered once, but the entry
                  is counted more than once based on the prevailing wage level:
                  Level I = 1 entry, Level II = 2, Level III = 3, Level IV = 4.
                  If multiple employers register the same person, USCIS uses the
                  lowest wage level submitted to calculate entries.
                </ExplanationText>
              </ProcessStep>
              <ProcessStep>
                <ExplanationText>
                  <ProcessStepTitle>
                    Submission &amp; Adjudication:
                  </ProcessStepTitle>{" "}
                  When filing a petition for a selected applicant, employers
                  must submit an LCA showing the same SOC, wage, and location as
                  in registration. USCIS verifies the wage still meets or
                  exceeds the selected level. Lowering the wage level via an
                  amendment or extension filed later can lead to denial or
                  revocation.
                </ExplanationText>
              </ProcessStep>

              <ExplanationText>
                For more information on potential strategies for companies to
                increase their selection odds, read our full newsletter on the
                subject{" "}
                <NewsletterLink
                  href="https://lawfullyintelligence.beehiiv.com/p/immigration-intel-december-18-2025"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </NewsletterLink>
                .
              </ExplanationText>
            </ExplanationSection>

            <Subtitle>
              Simulate your selection odds under the proposed weighted H-1B
              lottery system. Enter your applications by wage level and select a
              scenario to see your expected outcomes.
            </Subtitle>

            {/* Registrations Submitted Section */}
            <SectionTitle>Registrations submitted:</SectionTitle>
            <InputGrid>
              <InputBox>
                <Label>Level I</Label>
                <Input
                  type="number"
                  min="0"
                  value={getDisplayValue(userEntries.levelI)}
                  onChange={(e) => handleInputChange("levelI", e.target.value)}
                  placeholder="0"
                />
              </InputBox>
              <InputBox>
                <Label>Level II</Label>
                <Input
                  type="number"
                  min="0"
                  value={getDisplayValue(userEntries.levelII)}
                  onChange={(e) => handleInputChange("levelII", e.target.value)}
                  placeholder="0"
                />
              </InputBox>
              <InputBox>
                <Label>Level III</Label>
                <Input
                  type="number"
                  min="0"
                  value={getDisplayValue(userEntries.levelIII)}
                  onChange={(e) =>
                    handleInputChange("levelIII", e.target.value)
                  }
                  placeholder="0"
                />
              </InputBox>
              <InputBox>
                <Label>Level IV</Label>
                <Input
                  type="number"
                  min="0"
                  value={getDisplayValue(userEntries.levelIV)}
                  onChange={(e) => handleInputChange("levelIV", e.target.value)}
                  placeholder="0"
                />
              </InputBox>
            </InputGrid>

            {/* Scenario Selection */}
            <SectionTitle>Select a scenario:</SectionTitle>
            <ScenarioGrid>
              <ScenarioBox
                isSelected={
                  selectedScenario === "randomized" && !isCustomScenario
                }
                isDisabled={isCustomScenario}
                onClick={() =>
                  !isCustomScenario && setSelectedScenario("randomized")
                }
              >
                <ScenarioLabel>Randomized (Old System)</ScenarioLabel>
                <ScenarioValue>343,981</ScenarioValue>
              </ScenarioBox>
              <ScenarioBox
                isSelected={
                  selectedScenario === "baseline" && !isCustomScenario
                }
                isDisabled={isCustomScenario}
                onClick={() =>
                  !isCustomScenario && setSelectedScenario("baseline")
                }
              >
                <ScenarioLabel>Baseline</ScenarioLabel>
                <ScenarioValue>343,981</ScenarioValue>
              </ScenarioBox>
              <ScenarioBox
                isSelected={
                  selectedScenario === "scenarioA" && !isCustomScenario
                }
                isDisabled={isCustomScenario}
                onClick={() =>
                  !isCustomScenario && setSelectedScenario("scenarioA")
                }
              >
                <ScenarioLabel>15% Drop</ScenarioLabel>
                <ScenarioValue>292,384</ScenarioValue>
              </ScenarioBox>
              <ScenarioBox
                isSelected={
                  selectedScenario === "scenarioB" && !isCustomScenario
                }
                isDisabled={isCustomScenario}
                onClick={() =>
                  !isCustomScenario && setSelectedScenario("scenarioB")
                }
              >
                <ScenarioLabel>30% Drop</ScenarioLabel>
                <ScenarioValue>240,787</ScenarioValue>
              </ScenarioBox>
              <ScenarioBox
                isSelected={
                  selectedScenario === "scenarioD" && !isCustomScenario
                }
                isDisabled={isCustomScenario}
                onClick={() =>
                  !isCustomScenario && setSelectedScenario("scenarioD")
                }
              >
                <ScenarioLabel>50% Drop</ScenarioLabel>
                <ScenarioValue>171,991</ScenarioValue>
              </ScenarioBox>
            </ScenarioGrid>

            {/* Custom Scenario */}
            <CustomScenarioContainer>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={isCustomScenario}
                  onChange={(e) => handleCustomToggle(e.target.checked)}
                />
                <CheckboxLabel>Enable Custom Scenario</CheckboxLabel>
              </CheckboxContainer>

              {isCustomScenario && (
                <SliderContainer>
                  <SliderRow>
                    <Slider
                      type="range"
                      min="1"
                      max="470000"
                      step="1000"
                      value={customTotalRegistrations}
                      onChange={(e) =>
                        setCustomTotalRegistrations(
                          parseInt(e.target.value, 10),
                        )
                      }
                    />
                    <SliderValue>
                      {customTotalRegistrations.toLocaleString()}
                    </SliderValue>
                  </SliderRow>
                  <SliderLabels>
                    <span>1</span>
                    <span>470,000</span>
                  </SliderLabels>
                  <CustomBreakdown>
                    <BreakdownItem>
                      <BreakdownLabel>Level I (40%)</BreakdownLabel>
                      <BreakdownValue>
                        {customRegistrationsByLevel.levelI.toLocaleString()}
                      </BreakdownValue>
                    </BreakdownItem>
                    <BreakdownItem>
                      <BreakdownLabel>Level II (42%)</BreakdownLabel>
                      <BreakdownValue>
                        {customRegistrationsByLevel.levelII.toLocaleString()}
                      </BreakdownValue>
                    </BreakdownItem>
                    <BreakdownItem>
                      <BreakdownLabel>Level III (11%)</BreakdownLabel>
                      <BreakdownValue>
                        {customRegistrationsByLevel.levelIII.toLocaleString()}
                      </BreakdownValue>
                    </BreakdownItem>
                    <BreakdownItem>
                      <BreakdownLabel>Level IV (7%)</BreakdownLabel>
                      <BreakdownValue>
                        {customRegistrationsByLevel.levelIV.toLocaleString()}
                      </BreakdownValue>
                    </BreakdownItem>
                  </CustomBreakdown>
                </SliderContainer>
              )}
            </CustomScenarioContainer>

            {/* Scenario Data Display */}
            {!isCustomScenario && (
              <>
                <SectionTitle>
                  {currentScenario.name} - Registration Distribution
                </SectionTitle>
                <WageLevelRow>
                  <WageLevelLabel>Wage Level</WageLevelLabel>
                  <DataValue>Level I</DataValue>
                  <DataValue>Level II</DataValue>
                  <DataValue>Level III</DataValue>
                  <DataValue>Level IV</DataValue>
                </WageLevelRow>
                <WageLevelRow>
                  <WageLevelLabel>Registrations</WageLevelLabel>
                  <DataBox>
                    <DataValue>
                      {currentScenario.registrationsByLevel.levelI.toLocaleString()}
                    </DataValue>
                  </DataBox>
                  <DataBox>
                    <DataValue>
                      {currentScenario.registrationsByLevel.levelII.toLocaleString()}
                    </DataValue>
                  </DataBox>
                  <DataBox>
                    <DataValue>
                      {currentScenario.registrationsByLevel.levelIII.toLocaleString()}
                    </DataValue>
                  </DataBox>
                  <DataBox>
                    <DataValue>
                      {currentScenario.registrationsByLevel.levelIV.toLocaleString()}
                    </DataValue>
                  </DataBox>
                </WageLevelRow>
              </>
            )}

            {/* Your Odds Section */}
            <SectionTitle>Your odds:</SectionTitle>
            <WageLevelRow>
              <WageLevelLabel>Expected Selections</WageLevelLabel>
              <OddsBox>
                <OddsValue>
                  {Math.round(calculateResults.userSelectionsByLevel.levelI)}
                  {userEntries.levelI > 0 && (
                    <div style={{ fontSize: "12px", marginTop: "4px" }}>
                      (
                      {Math.min(
                        100,
                        calculateResults.levelProbabilities.levelI,
                      ).toFixed(1)}
                      %)
                    </div>
                  )}
                </OddsValue>
              </OddsBox>
              <OddsBox>
                <OddsValue>
                  {Math.round(calculateResults.userSelectionsByLevel.levelII)}
                  {userEntries.levelII > 0 && (
                    <div style={{ fontSize: "12px", marginTop: "4px" }}>
                      (
                      {Math.min(
                        100,
                        calculateResults.levelProbabilities.levelII,
                      ).toFixed(1)}
                      %)
                    </div>
                  )}
                </OddsValue>
              </OddsBox>
              <OddsBox>
                <OddsValue>
                  {Math.round(calculateResults.userSelectionsByLevel.levelIII)}
                  {userEntries.levelIII > 0 && (
                    <div style={{ fontSize: "12px", marginTop: "4px" }}>
                      (
                      {Math.min(
                        100,
                        calculateResults.levelProbabilities.levelIII,
                      ).toFixed(1)}
                      %)
                    </div>
                  )}
                </OddsValue>
              </OddsBox>
              <OddsBox>
                <OddsValue>
                  {Math.round(calculateResults.userSelectionsByLevel.levelIV)}
                  {userEntries.levelIV > 0 && (
                    <div style={{ fontSize: "12px", marginTop: "4px" }}>
                      (
                      {Math.min(
                        100,
                        calculateResults.levelProbabilities.levelIV,
                      ).toFixed(1)}
                      %)
                    </div>
                  )}
                </OddsValue>
              </OddsBox>
            </WageLevelRow>
            <WageLevelRow>
              <WageLevelLabel>Total</WageLevelLabel>
              <div
                style={{
                  gridColumn: "2 / -1",
                  padding: "16px",
                  background: "#195dee",
                  borderRadius: "8px",
                  textAlign: "center",
                  color: "white",
                }}
              >
                <div style={{ fontSize: "24px", fontWeight: "700" }}>
                  {calculateResults.expectedSelections} expected selections
                </div>
                <div style={{ fontSize: "14px", marginTop: "4px" }}>
                  out of {calculateResults.totalUserEntries} total applications
                  entered
                </div>
              </div>
            </WageLevelRow>
          </SimulatorCard>

          {/* Weighted Lottery Background */}
          <InfoSection>
            <InfoTitle>Weighted lottery</InfoTitle>
            <InfoText>
              <strong>Background context:</strong> The proposed weighted H-1B
              lottery system assigns different weights to applications based on
              their prevailing wage level:
            </InfoText>
            <InfoList>
              <InfoListItem>Level I = 1 entry (counted once)</InfoListItem>
              <InfoListItem>Level II = 2 entries (counted twice)</InfoListItem>
              <InfoListItem>
                Level III = 3 entries (counted three times)
              </InfoListItem>
              <InfoListItem>
                Level IV = 4 entries (counted four times)
              </InfoListItem>
            </InfoList>
            <InfoText>
              <strong>Wage levels:</strong> Prevailing wage levels are
              determined by the Occupational Employment and Wage Statistics
              (OEWS) system based on the SOC code, work location, and offered
              wage. Higher wage levels generally correspond to more experienced
              positions with higher salaries.
            </InfoText>
          </InfoSection>

          {/* Guide Section */}
          <InfoSection>
            <InfoTitle>Guide section</InfoTitle>
            <InfoText>
              <strong>Randomized (Old System):</strong> This scenario simulates
              the previous lottery system where all applicants had equal odds
              (34.9%) regardless of wage level. Use this to compare outcomes
              between the old random selection and the new weighted system.
            </InfoText>
            <InfoText>
              <strong>Weighted scenarios:</strong> All other scenarios use
              WEIGHTED lottery logic where higher wage levels get better odds
              through multiple weighted entries (Level I = 1×, Level II = 2×,
              Level III = 3×, Level IV = 4×).
            </InfoText>
            <InfoText>
              <strong>Registration distribution:</strong> All scenarios use the
              same distribution across wage levels: Level I = 40%, Level II =
              42%, Level III = 11%, Level IV = 7%.
            </InfoText>
            <InfoText>
              <strong>Baseline (343,981 registrations):</strong> No change in
              total registrations from FY 2026 levels using the weighted system.
              This represents the expected pool size if registration patterns
              remain similar to last year.
            </InfoText>
            <InfoText>
              <strong>15% Drop (292,384 registrations):</strong> Simulates a
              moderate decrease in total registrations, potentially due to
              economic conditions or policy uncertainty.
            </InfoText>
            <InfoText>
              <strong>30% Drop (240,787 registrations):</strong> Simulates a
              significant decrease in total registrations, reflecting a more
              cautious market response.
            </InfoText>
            <InfoText>
              <strong>50% Drop (171,991 registrations):</strong> Simulates a
              dramatic decrease in total registrations, potentially due to major
              policy changes like the proposed $100k fee or other significant
              market shifts.
            </InfoText>
            <InfoText>
              <strong>Custom Scenario:</strong> Enable the custom scenario
              checkbox to set any total registration count from 1 to 470,000
              using the slider. The wage level distribution (40% Level I, 42%
              Level II, 11% Level III, 7% Level IV) remains constant while you
              adjust the total pool size.
            </InfoText>
          </InfoSection>

          {/* Custom Report Section */}
          <InfoSection>
            <InfoTitle>Lawfully Intelligence</InfoTitle>
            <InfoText>
              This simulator is based on analysis from{" "}
              <strong>Lawfully Intelligence</strong>, our premium newsletter
              providing weekly insights on immigration processing trends, policy
              changes, and data-driven analysis.
            </InfoText>
            <InfoText>
              Want to run custom simulations using your portfolio data or need
              help analyzing your specific situation? Contact us at{" "}
              <a
                href="mailto:finn@lawfully.com"
                style={{ color: "#195dee", textDecoration: "underline" }}
              >
                finnreynolds@lawfully.com
              </a>
            </InfoText>
          </InfoSection>
        </ContentArea>
      </PageContainer>
    </PageLayout>
  );
};

export default H1BLotterySimulator;
